import React, { useState, useEffect } from "react";
import { Icon } from "semantic-ui-react";
import {
  ListEnvironmentalRiskMatrixForms,
  EnvironmentalRiskMatrixForm,
  RiskMatrixHeatMap,
  RiskMatrixGuide,
  IfrsList,
  IfrsForm,
  IfrsGuide,
  GriList,
  GriForm,
  GriGuide,
  Iso14091List,
  Iso14091Form,
  Iso14091Guide,
  EsrsGuide,
  EsrsDoubleMateriality,
  LeopoldList,
  LeopoldForm,
  LeopoldGuide,
  ConesaList,
  ConesaForm,
  ConesaGuide,
  RiskMatrixGeneralGuide,
  RiskMatrixDrawer,
  RiskMatrixWorkspace,
} from "../../../components/Admin/EnvironmentalRiskMatrixForms";
import {
  buildCoverageIso14001,
  buildCoverageIfrs,
  buildCoverageGri,
  buildCoverageIso14091,
  buildCoverageLeopold,
  buildCoverageConesa,
} from "../../../components/Admin/EnvironmentalRiskMatrixForms/riskMatrixCoverage";
import { useAuth } from "../../../hooks";
import { useLanguage, useSite } from "../../../contexts";
import { isAdmin, isMaster } from "../../../utils/checkPermission";
import {
  EnvironmentalRiskMatrixform,
  EnvironmentalRiskMatrixIfrsform,
  EnvironmentalRiskMatrixGriform,
  EnvironmentalRiskMatrixIso14091form,
  EnvironmentalRiskMatrixLeopoldform,
  EnvironmentalRiskMatrixConesaform,
} from "../../../api";
import "./EnvironmentalRiskMatrixForms.scss";

const environmentalRiskMatrixCtrl = new EnvironmentalRiskMatrixform();
const environmentalRiskMatrixIfrsCtrl = new EnvironmentalRiskMatrixIfrsform();
const environmentalRiskMatrixGriCtrl = new EnvironmentalRiskMatrixGriform();
const environmentalRiskMatrixIso14091Ctrl = new EnvironmentalRiskMatrixIso14091form();
const environmentalRiskMatrixLeopoldCtrl = new EnvironmentalRiskMatrixLeopoldform();
const environmentalRiskMatrixConesaCtrl = new EnvironmentalRiskMatrixConesaform();

// Cada organización trabaja con UN estándar a la vez (ver README del
// handoff) — el ☰ (RiskMatrixDrawer) deja elegir cuál ver, igual que en el
// prototipo. Agregar una metodología nueva = un entry acá (Guide/List/
// fetchBySite ya construidos en su propia carpeta) + sumarla también en
// RiskMatrixDrawer.js (ahí están las 7, con "Próximamente" para las que
// todavía no tienen entry acá).
const METHODOLOGIES = [
  {
    key: "iso14001",
    Guide: RiskMatrixGuide,
    List: ListEnvironmentalRiskMatrixForms,
    Form: EnvironmentalRiskMatrixForm,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixCtrl.getEnvironmentalRiskMatricesBySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixCtrl.createEnvironmentalRiskMatrix(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixCtrl.deleteEnvironmentalRiskMatrix(accessToken, id),
    buildCoverage: buildCoverageIso14001,
  },
  {
    key: "ifrs",
    Guide: IfrsGuide,
    List: IfrsList,
    Form: IfrsForm,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixIfrsCtrl.getEnvironmentalRiskMatricesIfrsBySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixIfrsCtrl.createEnvironmentalRiskMatrixIfrs(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixIfrsCtrl.deleteEnvironmentalRiskMatrixIfrs(accessToken, id),
    buildCoverage: buildCoverageIfrs,
  },
  {
    key: "gri",
    Guide: GriGuide,
    List: GriList,
    Form: GriForm,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixGriCtrl.getEnvironmentalRiskMatricesGriBySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixGriCtrl.createEnvironmentalRiskMatrixGri(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixGriCtrl.deleteEnvironmentalRiskMatrixGri(accessToken, id),
    buildCoverage: buildCoverageGri,
  },
  {
    key: "iso14091",
    Guide: Iso14091Guide,
    List: Iso14091List,
    Form: Iso14091Form,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixIso14091Ctrl.getEnvironmentalRiskMatricesIso14091BySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixIso14091Ctrl.createEnvironmentalRiskMatrixIso14091(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixIso14091Ctrl.deleteEnvironmentalRiskMatrixIso14091(accessToken, id),
    buildCoverage: buildCoverageIso14091,
  },
  {
    // Sin carga propia: integra automáticamente lo cargado en GRI (impacto)
    // e IFRS S2 (financiero) — ver EsrsDoubleMateriality. No usa
    // Items/Heat map genéricos, así que no tiene List/fetchBySite ni workspace
    // de 5 pasos (el prototipo tampoco lo tiene para ESRS).
    key: "esrs",
    isIntegrator: true,
    Guide: EsrsGuide,
    DoubleMateriality: EsrsDoubleMateriality,
  },
  {
    // Leopold y Conesa son EIA de proyecto (acción x factor), no risk
    // register de operación continua: no tienen heat map (Leopold no agrega
    // ningún índice; Conesa produce un único score I 13-100, no un par
    // prob/sev), así que van con hasHeatMap:false.
    key: "leopold",
    hasHeatMap: false,
    Guide: LeopoldGuide,
    List: LeopoldList,
    Form: LeopoldForm,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixLeopoldCtrl.getEnvironmentalRiskMatricesLeopoldBySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixLeopoldCtrl.createEnvironmentalRiskMatrixLeopold(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixLeopoldCtrl.deleteEnvironmentalRiskMatrixLeopold(accessToken, id),
    buildCoverage: buildCoverageLeopold,
  },
  {
    key: "conesa",
    hasHeatMap: false,
    Guide: ConesaGuide,
    List: ConesaList,
    Form: ConesaForm,
    fetchBySite: (accessToken, site) => environmentalRiskMatrixConesaCtrl.getEnvironmentalRiskMatricesConesaBySite(accessToken, site),
    createFn: (accessToken, payload) => environmentalRiskMatrixConesaCtrl.createEnvironmentalRiskMatrixConesa(accessToken, payload),
    deleteFn: (accessToken, id) => environmentalRiskMatrixConesaCtrl.deleteEnvironmentalRiskMatrixConesa(accessToken, id),
    buildCoverage: buildCoverageConesa,
  },
];

export function EnvironmentalRiskMatrixForms() {
  const {
    user: { role, site },
  } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [siteSelected, setSiteSelected] = useState(null);
  const [reload, setReload] = useState(false);
  const [activeTab, setActiveTab] = useState("items");
  const [methodologyKey, setMethodologyKey] = useState("iso14001");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showGeneralGuide, setShowGeneralGuide] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [dashboardItemCount, setDashboardItemCount] = useState(null);

  const siteContext = useSite().site;
  const onReload = () => setReload((prev) => !prev);

  useEffect(() => {
    if (siteContext !== "") setSiteSelected(siteContext);
  }, [siteContext]);

  useEffect(() => {
    if (site) setSiteSelected(site);
  }, [site]);

  // El heat map del Dashboard solo tiene sentido si ya hay ítems cargados
  // (si no, muestra una grilla coloreada pero vacía) — igual criterio que
  // renderDashPanel() del prototipo, que directamente no lo renderiza.
  useEffect(() => {
    setDashboardItemCount(null);
  }, [methodologyKey, siteSelected]);

  const showSiteWarning = siteSelected === null && (isMaster(role) || (isAdmin(role) && !site));
  const methodology = METHODOLOGIES.find((m) => m.key === methodologyKey);
  const { Guide, List, DoubleMateriality, isIntegrator } = methodology;
  const showHeatMap = !isIntegrator && methodology.hasHeatMap !== false && dashboardItemCount > 0;

  return (
    <div className="risk-matrix-page">
      <div className="risk-matrix-page__wrap">
        {!(workspaceOpen && !isIntegrator) && (
          <div className="risk-matrix-page__header">
            <div>
              <h1 className="risk-matrix-page__title">{t("risk_matrix_page_title")}</h1>
              <p className="risk-matrix-page__subtitle">{t("risk_matrix_page_subtitle")}</p>
            </div>
            <button
              type="button"
              className="risk-matrix-page__hamburger"
              onClick={() => setDrawerOpen(true)}
              aria-label="menu"
            >
              ☰
            </button>
          </div>
        )}

        {workspaceOpen && !isIntegrator ? (
          <RiskMatrixWorkspace
            methodologyKey={methodologyKey}
            tag={t(`risk_matrix_methodology_${methodologyKey}`)}
            title={t(`risk_matrix_methodology_${methodologyKey}_desc`)}
            onBack={() => setWorkspaceOpen(false)}
            Guide={Guide}
            Form={methodology.Form}
            List={List}
            fetchBySite={methodology.fetchBySite}
            hasHeatMap={methodology.hasHeatMap !== false}
            siteSelected={siteSelected}
            reload={reload}
            onReload={onReload}
            examplesKey={methodologyKey}
            createFn={methodology.createFn}
            deleteFn={methodology.deleteFn}
            buildCoverage={methodology.buildCoverage}
          />
        ) : (
          <>
            {/* Para las metodologías con workspace, la Guía ya está ahí (paso 1)
                — este selector de pestañas solo hace falta para ESRS, que no
                tiene workspace de 5 pasos. */}
            {!showGeneralGuide && isIntegrator && (
              <div className="risk-matrix-page__tabs">
                <button
                  type="button"
                  className={`risk-matrix-page__tab${activeTab === "guide" ? " active" : ""}`}
                  onClick={() => setActiveTab("guide")}
                >
                  {t("risk_matrix_tab_guide")}
                </button>
                <button
                  type="button"
                  className={`risk-matrix-page__tab${activeTab === "items" ? " active" : ""}`}
                  onClick={() => setActiveTab("items")}
                >
                  {t("risk_matrix_tab_dm")}
                </button>
              </div>
            )}

            {activeTab === "items" && !showGeneralGuide && !showSiteWarning && (
              <div className="risk-matrix-page__intro">
                <div>
                  <h2>{t("risk_matrix_dashboard_title")}</h2>
                  <p>{t("risk_matrix_dashboard_intro")}</p>
                </div>
                <button type="button" className="risk-matrix-page__intro-btn" onClick={() => setDrawerOpen(true)}>
                  ☰ {t("risk_matrix_change_methodology")}
                </button>
              </div>
            )}

            <div className="risk-matrix-page__panel">
              {showGeneralGuide ? (
                <RiskMatrixGeneralGuide onBack={() => setShowGeneralGuide(false)} />
              ) : showSiteWarning ? (
                <div className="risk-matrix-page__no-site">
                  <Icon name="warning sign" size="big" />
                  <p>{t("select_company_site_from_header")}</p>
                </div>
              ) : activeTab === "guide" ? (
                <Guide />
              ) : activeTab === "items" && isIntegrator ? (
                <>
                  <div className="risk-matrix-page__methodology-head">
                    <span className="risk-matrix-page__tag">{t(`risk_matrix_methodology_${methodologyKey}`)}</span>
                    <h2>{t(`risk_matrix_methodology_${methodologyKey}_desc`)}</h2>
                  </div>
                  <p className="risk-matrix-page__methodology-intro">{t(`risk_matrix_methodology_${methodologyKey}_intro`)}</p>
                  <DoubleMateriality siteSelected={siteSelected} reload={reload} />
                </>
              ) : activeTab === "items" ? (
                <>
                  <List
                    siteSelected={siteSelected}
                    reload={reload}
                    onReload={onReload}
                    onOpenAnalysis={() => setWorkspaceOpen(true)}
                    onItemsCount={setDashboardItemCount}
                  />
                  {showHeatMap && (
                    <div className="risk-matrix-page__heatmap">
                      <h3 className="risk-matrix-page__heatmap-title">{t("risk_matrix_tab_heatmap")}</h3>
                      <RiskMatrixHeatMap siteSelected={siteSelected} reload={reload} fetchBySite={methodology.fetchBySite} />
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </>
        )}
      </div>

      <RiskMatrixDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        methodologyKey={methodologyKey}
        onSelectMethodology={(key) => {
          setMethodologyKey(key);
          setActiveTab("items");
          setShowGeneralGuide(false);
          setWorkspaceOpen(false);
          setDrawerOpen(false);
        }}
        onGoDashboard={() => {
          setShowGeneralGuide(false);
          setActiveTab("items");
          setWorkspaceOpen(false);
          setDrawerOpen(false);
        }}
        onOpenGuide={() => {
          setShowGeneralGuide(true);
          setDrawerOpen(false);
        }}
      />
    </div>
  );
}
